import moment from 'moment';
import {
  LONG_TIME_NO_SEE_THRESHOLD,
  POLICY_DUE_SOON_THRESHOLD,
  QUEUE_SIGNALS,
  SIGNAL_RANK
} from '../constants/client-queue';
import { adviserFactsheetOf, qualifyingFactsheetOf } from './client-factsheet';
import type { QueueSignal } from '../constants/client-queue';
import type { PolicyDueEntry, QueueClient } from '../types/client';

export type QueueMeta = {
  daysUntil?: number;
  daysSince?: number;
  date?: string;
};

export type QueueEntry = {
  client: QueueClient;
  signal: QueueSignal;
  meta: QueueMeta;
};

export type BuildTodayQueueInput = {
  clients: QueueClient[];
  adviserEmail?: string;
  policyDue?: PolicyDueEntry[];
  now: moment.MomentInput;
};

/**
 * A date of birth's occurrence in one given year.
 *
 * A 29 February birthday is clamped to the last day of February in non-leap
 * years, so it lands on a day every year instead of silently disappearing.
 *
 * Lifted out of `nextBirthday` so the reminder calendar can ask about any year,
 * including years already past — `nextBirthday` only ever looks forwards, which
 * a calendar paged backwards cannot use. One rule, two readers.
 */
export const birthdayOccurrenceIn = (dob: string | null | undefined, year: number): moment.Moment | null => {
  if (!dob) {
    return null;
  }
  const parsed = moment.parseZone(dob);
  if (!parsed.isValid()) {
    return null;
  }

  const day = parsed.date();
  const firstOfMonth = moment({ year, month: parsed.month(), day: 1 }).startOf('day');
  return firstOfMonth.date(Math.min(day, firstOfMonth.daysInMonth()));
};

/**
 * The next occurrence of a date of birth, at or after `startOfToday`.
 */
export const nextBirthday = (dob: string | null | undefined, startOfToday: moment.Moment): moment.Moment | null => {
  const thisYear = birthdayOccurrenceIn(dob, startOfToday.year());
  if (!thisYear) {
    return null;
  }
  return thisYear.isBefore(startOfToday) ? birthdayOccurrenceIn(dob, startOfToday.year() + 1) : thisYear;
};

const detectSignal = (
  client: QueueClient,
  adviserEmail: string | undefined,
  startOfToday: moment.Moment,
  policyDueById: Map<string, string>
): QueueEntry | null => {
  // Two readers, following V1's own split (client-factsheet.ts): the reminder
  // and last-meeting signals are the two the assistant gates on the connection
  // (store/assistant/duck.js:224 via isClientRemindMe, clientPage.js:1775), so
  // they read the gated factsheet; the birthday does not, and neither does any
  // other adviser-owned field.
  const factsheet = qualifyingFactsheetOf(client, adviserEmail);
  const adviserFactsheet = adviserFactsheetOf(client, adviserEmail);

  if (factsheet?.fsReminderActive && factsheet.fsReminderDate) {
    const reminder = moment(factsheet.fsReminderDate, 'YYYY-MM-DD');
    if (reminder.isValid() && reminder.isSame(startOfToday, 'day')) {
      return {
        client,
        signal: QUEUE_SIGNALS.REMIND_TODAY,
        meta: { date: reminder.format('YYYY-MM-DD') }
      };
    }
  }

  const birthday = nextBirthday(adviserFactsheet?.dob, startOfToday);
  const daysUntilBirthday = birthday ? birthday.diff(startOfToday, 'days') : null;

  if (daysUntilBirthday !== null && daysUntilBirthday <= 1) {
    return {
      client,
      signal: QUEUE_SIGNALS.BIRTHDAY_IMMINENT,
      meta: { daysUntil: daysUntilBirthday }
    };
  }

  const policyDueDate = policyDueById.get(client._id);
  if (policyDueDate) {
    const due = moment(policyDueDate).startOf('day');
    const windowEnd = startOfToday.clone().add(POLICY_DUE_SOON_THRESHOLD);
    if (due.isValid() && !due.isBefore(startOfToday) && !due.isAfter(windowEnd)) {
      return {
        client,
        signal: QUEUE_SIGNALS.POLICY_DUE_SOON,
        meta: { daysUntil: due.diff(startOfToday, 'days'), date: due.format('YYYY-MM-DD') }
      };
    }
  }

  if (birthday && daysUntilBirthday !== null && birthday.isSame(startOfToday, 'month')) {
    return {
      client,
      signal: QUEUE_SIGNALS.BIRTHDAY_THIS_MONTH,
      meta: { daysUntil: daysUntilBirthday }
    };
  }

  if (factsheet?.latestMeetingDate) {
    const lastMeeting = moment(factsheet.latestMeetingDate).startOf('day');
    const cutoff = startOfToday.clone().subtract(LONG_TIME_NO_SEE_THRESHOLD);
    if (lastMeeting.isValid() && lastMeeting.isBefore(cutoff)) {
      return {
        client,
        signal: QUEUE_SIGNALS.LONG_TIME_NO_SEE,
        meta: { daysSince: startOfToday.diff(lastMeeting, 'days') }
      };
    }
  }

  return null;
};

/**
 * Signal priority first. Within one signal, the most urgent entry leads:
 * the soonest upcoming date, or the longest elapsed gap.
 */
const compareEntries = (left: QueueEntry, right: QueueEntry): number => {
  const byPriority = SIGNAL_RANK[left.signal] - SIGNAL_RANK[right.signal];
  if (byPriority !== 0) {
    return byPriority;
  }
  if (left.meta.daysUntil !== undefined && right.meta.daysUntil !== undefined) {
    return left.meta.daysUntil - right.meta.daysUntil;
  }
  if (left.meta.daysSince !== undefined && right.meta.daysSince !== undefined) {
    return right.meta.daysSince - left.meta.daysSince;
  }
  return 0;
};

export const buildTodayQueue = (input: BuildTodayQueueInput): QueueEntry[] => {
  const { clients, adviserEmail, policyDue, now } = input;
  const startOfToday = moment(now).startOf('day');

  // The server emits one entry per policy, not per client, sorted ascending by
  // due date (server/routes/advDashController.js). Keep the soonest per client
  // so a farther-out policy never masks a nearer one for the same client.
  const policyDueById = new Map<string, string>();
  (policyDue || []).forEach(entry => {
    if (!entry || !entry._id || !entry.policyDueDate) {
      return;
    }
    const candidate = moment(entry.policyDueDate);
    if (!candidate.isValid()) {
      return;
    }
    const existing = policyDueById.get(entry._id);
    if (!existing || candidate.isBefore(moment(existing))) {
      policyDueById.set(entry._id, entry.policyDueDate);
    }
  });

  return clients
    .map(client => detectSignal(client, adviserEmail, startOfToday, policyDueById))
    .filter((entry): entry is QueueEntry => entry !== null)
    .sort(compareEntries);
};
